import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpen, Plus, ChevronDown, ChevronRight, Youtube, FileText, Trash2, Pencil,
  Loader2, X, Save, ExternalLink, Video,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Extract YouTube video ID from any common URL format (client-side, for preview). */
const extractYoutubeId = (url) => {
  if (!url) return null;
  const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
};

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TutorialRow = ({ tutorial, canEdit, onDelete, onEdit, apiHeaders }) => {
  const [open, setOpen] = useState(false);
  const isYoutube = tutorial.kind === 'youtube';
  const yid = isYoutube ? (tutorial.youtube_id || extractYoutubeId(tutorial.youtube_url)) : null;

  const openPdf = async () => {
    try {
      const res = await axios.get(`${API}/tutorials/${tutorial.id}/file`, {
        headers: apiHeaders,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error('Erro ao abrir o PDF.');
    }
  };

  return (
    <div
      className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden"
      data-testid={`tutorial-row-${tutorial.id}`}
    >
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-800 transition-colors"
        data-testid={`tutorial-toggle-${tutorial.id}`}
      >
        <span className="shrink-0 text-slate-400">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
        <span className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
          isYoutube ? 'bg-red-500/15 text-red-400' : 'bg-teal-500/15 text-teal-400'
        }`}>
          {isYoutube ? <Youtube className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{tutorial.title}</p>
          {tutorial.description && (
            <p className="text-slate-400 text-xs truncate">{tutorial.description}</p>
          )}
        </div>
        <span className="hidden sm:inline text-[10px] uppercase tracking-wide text-slate-500 shrink-0">
          {isYoutube ? 'YouTube' : `PDF · ${formatSize(tutorial.file_size)}`}
        </span>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => onEdit(tutorial)}
              className="p-1.5 text-slate-400 hover:text-teal-400 rounded"
              title="Editar"
              data-testid={`tutorial-edit-${tutorial.id}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => onDelete(tutorial)}
              className="p-1.5 text-slate-400 hover:text-red-400 rounded"
              title="Excluir"
              data-testid={`tutorial-delete-${tutorial.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </button>

      {open && (
        <div className="border-t border-slate-700 p-4 space-y-3">
          {tutorial.description && (
            <p className="text-sm text-slate-300 whitespace-pre-line">{tutorial.description}</p>
          )}
          {isYoutube && yid && (
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${yid}`}
                title={tutorial.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid={`tutorial-youtube-${tutorial.id}`}
              />
            </div>
          )}
          {isYoutube && tutorial.youtube_url && (
            <a
              href={tutorial.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300"
            >
              <ExternalLink className="h-3 w-3" /> Abrir no YouTube
            </a>
          )}
          {!isYoutube && (
            <button
              onClick={openPdf}
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              data-testid={`tutorial-open-pdf-${tutorial.id}`}
            >
              <FileText className="h-4 w-4" /> Abrir PDF ({tutorial.file_name || 'arquivo'})
            </button>
          )}
          <p className="text-[11px] text-slate-500">
            Publicado por {tutorial.created_by_name || '—'}
          </p>
        </div>
      )}
    </div>
  );
};

const TutorialForm = ({ open, onClose, editing, onSaved, apiHeaders }) => {
  const [kind, setKind] = useState('youtube');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setKind(editing.kind);
      setTitle(editing.title || '');
      setDescription(editing.description || '');
      setYoutubeUrl(editing.youtube_url || '');
      setFile(null);
    } else {
      setKind('youtube');
      setTitle(''); setDescription(''); setYoutubeUrl(''); setFile(null);
    }
  }, [open, editing]);

  const submit = async () => {
    const t = title.trim();
    if (!t) return toast.error('Título obrigatório.');
    setSaving(true);
    try {
      if (editing) {
        const body = { title: t, description };
        if (kind === 'youtube') body.youtube_url = youtubeUrl;
        await axios.put(`${API}/tutorials/${editing.id}`, body, { headers: apiHeaders });
        toast.success('Tutorial atualizado!');
      } else {
        if (kind === 'youtube' && !extractYoutubeId(youtubeUrl)) {
          setSaving(false);
          return toast.error('URL do YouTube inválida.');
        }
        if (kind === 'pdf' && !file) {
          setSaving(false);
          return toast.error('Selecione um arquivo PDF.');
        }
        const fd = new FormData();
        fd.append('title', t);
        fd.append('description', description);
        fd.append('kind', kind);
        if (kind === 'youtube') fd.append('youtube_url', youtubeUrl);
        if (kind === 'pdf' && file) fd.append('file', file);
        await axios.post(`${API}/tutorials`, fd, { headers: apiHeaders });
        toast.success('Tutorial criado!');
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tutorial-form-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">
            {editing ? 'Editar tutorial' : 'Novo tutorial'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="tutorial-form-close">
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="p-5 space-y-4">
          {!editing && (
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setKind('youtube')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  kind === 'youtube'
                    ? 'bg-red-500/15 border-red-500 text-red-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
                data-testid="tutorial-kind-youtube"
              >
                <Youtube className="h-4 w-4" /> YouTube
              </button>
              <button
                onClick={() => setKind('pdf')}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  kind === 'pdf'
                    ? 'bg-teal-500/15 border-teal-500 text-teal-300'
                    : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
                data-testid="tutorial-kind-pdf"
              >
                <FileText className="h-4 w-4" /> PDF
              </button>
            </div>
          )}
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Título</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
              data-testid="tutorial-form-title"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Descrição (opcional)</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500 resize-none"
              data-testid="tutorial-form-description"
            />
          </div>
          {kind === 'youtube' ? (
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">URL do YouTube</label>
              <input
                type="url"
                value={youtubeUrl}
                onChange={e => setYoutubeUrl(e.target.value)}
                placeholder="https://youtu.be/… ou https://www.youtube.com/watch?v=…"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-teal-500 font-mono"
                data-testid="tutorial-form-youtube-url"
              />
            </div>
          ) : !editing && (
            <div>
              <label className="block text-xs uppercase tracking-wide text-slate-400 mb-1">Arquivo PDF (máx. 20 MB)</label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={e => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-500 file:text-white hover:file:bg-teal-600"
                data-testid="tutorial-form-file"
              />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name} — {formatSize(file.size)}</p>}
            </div>
          )}
        </div>
        <footer className="px-5 py-4 border-t border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg"
            data-testid="tutorial-form-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-5 py-2 bg-teal-500 hover:bg-teal-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg flex items-center gap-2"
            data-testid="tutorial-form-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>
  );
};

const Tutoriais = () => {
  const { getAuthHeaders, hasPerm } = useAuth();
  const canEdit = hasPerm('create_edit_projects');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/tutorials`, { headers: getAuthHeaders() });
      setItems(res.data || []);
    } catch {
      toast.error('Erro ao carregar tutoriais');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (tut) => {
    if (!window.confirm(`Excluir "${tut.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/tutorials/${tut.id}`, { headers: getAuthHeaders() });
      toast.success('Tutorial excluído.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir.');
    }
  };

  const startEdit = (tut) => { setEditing(tut); setShowForm(true); };
  const startCreate = () => { setEditing(null); setShowForm(true); };

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="tutoriais-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-teal-400" />
              Tutoriais
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              Aprenda a usar o sistema, dublar melhor e resolver dúvidas frequentes.
            </p>
          </div>
          {canEdit && (
            <button
              onClick={startCreate}
              className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              data-testid="new-tutorial-btn"
            >
              <Plus className="h-5 w-5" /> Novo tutorial
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-slate-800/40 border border-dashed border-slate-700 rounded-2xl px-6 py-14 text-center">
            <Video className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-white font-medium">Nenhum tutorial ainda</p>
            <p className="text-slate-500 text-sm mt-1">
              {canEdit
                ? 'Comece adicionando um vídeo do YouTube ou um PDF.'
                : 'Aguarde o time publicar os primeiros tutoriais.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="tutorials-list">
            {items.map(t => (
              <TutorialRow
                key={t.id}
                tutorial={t}
                canEdit={canEdit}
                onEdit={startEdit}
                onDelete={handleDelete}
                apiHeaders={getAuthHeaders()}
              />
            ))}
          </div>
        )}
      </div>

      <TutorialForm
        open={showForm}
        onClose={() => setShowForm(false)}
        editing={editing}
        onSaved={load}
        apiHeaders={getAuthHeaders()}
      />
    </Layout>
  );
};

export default Tutoriais;
